import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { isSystemApp } from '@/lib/apps';

export type App = Database['public']['Tables']['apps']['Row'];
export type UserInstalledApp = Database['public']['Tables']['user_installed_apps']['Row'] & {
  app?: App;
};

export interface AppCategory {
  id: string;
  name: string;
  display_name: string;
  icon_name: string | null;
  description: string | null;
  sort_order: number;
}

class AppService {
  // Get all published apps from the store
  async getPublishedApps(): Promise<App[]> {
    const { data, error } = await supabase
      .from('apps')
      .select('*')
      .eq('is_published', true)
      .order('name');

    if (error) {
      console.error('Error fetching published apps:', error);
      return [];
    }

    return data || [];
  }

  // Get user's installed apps with system apps always included
  async getUserInstalledApps(userProfileId: string): Promise<UserInstalledApp[]> {
    const { data, error } = await supabase
      .from('user_installed_apps')
      .select(`
        *,
        app:apps(*)
      `)
      .eq('user_id', userProfileId);

    if (error) {
      console.error('Error fetching user installed apps:', error);
      return [];
    }

    // Get system apps that should always be installed
    const { data: systemApps, error: systemError } = await supabase
      .from('apps')
      .select('*')
      .eq('is_published', true)
      .contains('metadata', { system: true });

    if (systemError) {
      console.error('Error fetching system apps:', error);
      return data || [];
    }

    const installedApps = data || [];
    const systemAppsAsInstalled: UserInstalledApp[] = (systemApps || []).map(app => ({
      id: crypto.randomUUID(),
      user_id: userProfileId,
      app_id: app.id,
      is_in_dock: true, // System apps always in dock
      dock_position: null,
      user_config: {},
      installed_at: new Date().toISOString(),
      last_used_at: null,
      app: app
    }));

    // Filter out any system apps that are already explicitly installed
    const systemAppIds = new Set(systemAppsAsInstalled.map(sa => sa.app_id));
    const filteredInstalledApps = installedApps.filter(ia => !systemAppIds.has(ia.app_id));

    return [...systemAppsAsInstalled, ...filteredInstalledApps];
  }

  // Install an app for a user
  async installApp(userProfileId: string, appId: string): Promise<boolean> {
    const { error } = await supabase
      .from('user_installed_apps')
      .insert({
        user_id: userProfileId,
        app_id: appId,
        is_in_dock: false,
        user_config: {}
      });

    if (error) {
      console.error('Error installing app:', error);
      return false;
    }

    // Update download count
    await this.incrementDownloadCount(appId);
    
    return true;
  }

  // Uninstall an app for a user (prevents uninstalling system apps)
  async uninstallApp(userProfileId: string, appId: string): Promise<boolean> {
    // Check if this is a system app first
    const { data: app, error: appError } = await supabase
      .from('apps')
      .select('metadata')
      .eq('id', appId)
      .single();

    if (appError) {
      console.error('Error checking app type:', appError);
      return false;
    }

    // Prevent uninstalling system apps
    if (isSystemApp(app as any)) {
      console.log('Cannot uninstall system apps');
      return false;
    }

    const { error } = await supabase
      .from('user_installed_apps')
      .delete()
      .eq('user_id', userProfileId)
      .eq('app_id', appId);

    if (error) {
      console.error('Error uninstalling app:', error);
      return false;
    }

    return true;
  }

  // Add app to dock
  async addToDock(userProfileId: string, appId: string): Promise<boolean> {
    const { error } = await supabase
      .from('user_installed_apps')
      .update({ is_in_dock: true })
      .eq('user_id', userProfileId)
      .eq('app_id', appId);

    if (error) {
      console.error('Error adding app to dock:', error);
      return false;
    }

    return true;
  }

  // Remove app from dock (prevents removing system apps)
  async removeFromDock(userProfileId: string, appId: string): Promise<boolean> {
    // Check if this is a system app first
    const { data: app, error: appError } = await supabase
      .from('apps')
      .select('metadata')
      .eq('id', appId)
      .single();

    if (appError) {
      console.error('Error checking app type:', appError);
      return false;
    }

    // Prevent removing system apps from dock
    if (isSystemApp(app as any)) {
      console.log('Cannot remove system apps from dock');
      return false;
    }

    const { error } = await supabase
      .from('user_installed_apps')
      .update({ is_in_dock: false, dock_position: null })
      .eq('user_id', userProfileId)
      .eq('app_id', appId);

    if (error) {
      console.error('Error removing app from dock:', error);
      return false;
    }

    return true;
  }

  // Update last used timestamp
  async updateLastUsed(userProfileId: string, appId: string): Promise<void> {
    await supabase
      .from('user_installed_apps')
      .update({ last_used_at: new Date().toISOString() })
      .eq('user_id', userProfileId)
      .eq('app_id', appId);
  }

  // Get app categories
  async getCategories(): Promise<AppCategory[]> {
    const { data, error } = await supabase
      .from('app_categories')
      .select('*')
      .order('sort_order');

    if (error) {
      console.error('Error fetching categories:', error);
      return [];
    }

    return data || [];
  }

  // Get user profile ID from Clerk user ID
  async getUserProfileId(clerkUserId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('clerk_user_id', clerkUserId)
      .single();

    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }

    return data?.id || null;
  }

  // Get organization's installed apps via edge function
  async getOrganizationInstalledApps(organizationId: string): Promise<any[]> {
    console.log('🔍 Fetching org apps via edge function for:', organizationId);
    
    try {
      const { data, error } = await supabase.functions.invoke('org-management', {
        body: {
          action: 'get_org_apps',
          organizationId,
          clerkUserId: await this.getClerkUserId()
        }
      });

      if (error) throw error;
      
      console.log('📊 Org apps response:', data);
      console.log('📊 Org apps count:', data?.apps?.length || 0);
      
      return data?.apps || [];
    } catch (error) {
      console.error('❌ Error fetching organization apps:', error);
      return [];
    }
  }

  // Install an app for an organization via edge function
  async installAppForOrganization(appId: string, organizationId: string, installedBy: string): Promise<boolean> {
    console.log('📦 AppService.installAppForOrganization called:', { appId, organizationId, installedBy });
    
    try {
      const clerkUserId = await this.getClerkUserId();
      console.log('🔑 Clerk user ID:', clerkUserId);
      
      console.log('📤 Invoking edge function: org-management with action: install_org_app');
      const { data, error } = await supabase.functions.invoke('org-management', {
        body: {
          action: 'install_org_app',
          organizationId,
          appId,
          profileId: installedBy,
          clerkUserId
        }
      });
      
      console.log('📥 Edge function response:', { data, error });

      if (error) {
        console.error('❌ Error from edge function:', error);
        throw error;
      }
      
      const success = data?.ok === true;
      console.log(success ? '✅ AppService: App installed' : '❌ AppService: Installation failed');
      return success;
    } catch (error) {
      console.error('🔥 Exception in AppService.installAppForOrganization:', error);
      return false;
    }
  }

  // Uninstall an app for an organization via edge function
  async uninstallAppForOrganization(appId: string, organizationId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('org-management', {
        body: {
          action: 'uninstall_org_app',
          organizationId,
          appId,
          clerkUserId: await this.getClerkUserId()
        }
      });

      if (error) {
        console.error('Error from edge function:', error);
        return false;
      }
      return data?.ok === true;
    } catch (error) {
      console.error('Error uninstalling app for organization:', error);
      return false;
    }
  }

  // Add app to dock for organization via edge function
  async addAppToDock(appId: string, organizationId: string): Promise<boolean> {
    console.log('🚀 AppService.addAppToDock called:', { appId, organizationId });
    
    try {
      const clerkUserId = await this.getClerkUserId();
      console.log('🔑 Clerk user ID:', clerkUserId);
      
      console.log('📤 Invoking edge function: org-management with action: dock_org_app');
      const { data, error } = await supabase.functions.invoke('org-management', {
        body: {
          action: 'dock_org_app',
          organizationId,
          appId,
          clerkUserId
        }
      });
      
      console.log('📥 Edge function response:', { data, error });

      if (error) {
        console.error('❌ Error from edge function:', error);
        throw error;
      }
      
      const success = data?.ok === true;
      console.log(success ? '✅ AppService: App added to dock' : '❌ AppService: Failed to add to dock');
      return success;
    } catch (error) {
      console.error('🔥 Exception in AppService.addAppToDock:', error);
      return false;
    }
  }

  // Remove app from dock for organization via edge function
  async removeAppFromDock(appId: string, organizationId: string): Promise<boolean> {
    console.log('🔧 AppService.removeAppFromDock called:', { appId, organizationId });
    
    try {
      const clerkUserId = await this.getClerkUserId();
      console.log('🔑 Clerk user ID:', clerkUserId);
      
      console.log('📤 Invoking edge function: org-management with action: undock_org_app');
      const { data, error } = await supabase.functions.invoke('org-management', {
        body: {
          action: 'undock_org_app',
          organizationId,
          appId,
          clerkUserId
        }
      });
      
      console.log('📥 Edge function response:', { data, error });

      if (error) {
        console.error('❌ Error from edge function:', error);
        return false;
      }
      
      const success = data?.ok === true;
      console.log(success ? '✅ AppService: Removal successful' : '❌ AppService: Removal failed');
      return success;
    } catch (error) {
      console.error('🔥 Exception in AppService.removeAppFromDock:', error);
      return false;
    }
  }

  // Update last used timestamp for organization app via edge function
  async updateAppLastUsed(appId: string, organizationId: string): Promise<void> {
    try {
      await supabase.functions.invoke('org-management', {
        body: {
          action: 'touch_org_app',
          organizationId,
          appId,
          clerkUserId: await this.getClerkUserId()
        }
      });
    } catch (error) {
      console.error('Error updating last used timestamp:', error);
    }
  }

  // Helper to get current Clerk user ID
  private async getClerkUserId(): Promise<string> {
    // Prefer explicit persisted value set by OrganizationContext
    try {
      const id = localStorage.getItem('clerk_user_id');
      if (id) return id;
    } catch {}

    // Fallback: attempt to read Clerk JWT if available
    const jwt = localStorage.getItem('__clerk_client_jwt');
    if (jwt) {
      try {
        const payload = JSON.parse(atob(jwt.split('.')[1]));
        return payload.sub;
      } catch (e) {
        console.error('Failed to parse JWT:', e);
      }
    }
    return '';
  }
  private async incrementDownloadCount(appId: string): Promise<void> {
    const { data, error } = await supabase
      .from('apps')
      .select('download_count')
      .eq('id', appId)
      .single();

    if (!error && data) {
      await supabase
        .from('apps')
        .update({ download_count: data.download_count + 1 })
        .eq('id', appId);
    }
  }

  // Search apps
  async searchApps(query: string): Promise<App[]> {
    const { data, error } = await supabase
      .from('apps')
      .select('*')
      .eq('is_published', true)
      .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
      .order('name');

    if (error) {
      console.error('Error searching apps:', error);
      return [];
    }

    return data || [];
  }

  // Get apps by category
  async getAppsByCategory(category: string): Promise<App[]> {
    const { data, error } = await supabase
      .from('apps')
      .select('*')
      .eq('is_published', true)
      .eq('category', category)
      .order('name');

    if (error) {
      console.error('Error fetching apps by category:', error);
      return [];
    }

    return data || [];
  }
}

export const appService = new AppService();